import { v4 as uuidv4 } from 'uuid';
import React from "react";

/**
 * WebSocketClient is a React component that manages a WebSocket connection.
 * It handles the initialization, reconnection, and cleanup of the WebSocket.
 * 
 * Props:
 * @param {string} url - The WebSocket URL to connect to.
 * @param {string} [name] - Optional name for the WebSocket connection.
 * @param {function} [setMessageCallback] - Optional callback function to handle incoming messages.
 * @param {function} [setWsCallback] - Optional callback function to handle WebSocket instance.
 * 
 * State:
 * @property {number} readyState - The current ready state of the WebSocket connection.
 * @property {number} error - The error state of the WebSocket connection.
 * 
 * Methods:
 * @method init - Initializes the WebSocket connection.
 * @method deInit - Closes the WebSocket connection.
 * @method reconnect - Reconnects the WebSocket if the connection is closed.
 * 
 * Lifecycle Methods:
 * @method componentDidMount - Called after the component is mounted, initializes the WebSocket connection.
 * @method componentDidUpdate - Called after the component updates, checks and reconnects the WebSocket if needed.
 * @method componentWillUnmount - Called before the component is unmounted, closes the WebSocket connection.
 * 
 * Render:
 * @method render - Renders the component.
 */
class WebSocketClient extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            readyState: 0,
            error: 0,
            reconnect: true
        }

        this.reconnectId = null;
        this.refreshTokenId = null;
        this.ws = null;

        this.init = this.init.bind(this);
        this.deInit = this.deInit.bind(this);
        this.reconnect = this.reconnect.bind(this);
        this.sendRefreshTokenRequest = this.sendRefreshTokenRequest.bind(this);
        this.refreshToken = this.refreshToken.bind(this);
    }

    init() {
        if (!this.ws) {
            this.ws = new WebSocket(this.props.url);
            if (this?.props?.name) {
                this.ws.name = this.props.name;
            }
            if (!this?.ws?.id) {
                this.ws.id = uuidv4();
            }

            this.ws.onopen = () => {
                // console.log('ws connection established');
                this.setState({ readyState: this.ws.readyState });
                if (this.props?.token) {
                    let message = {};
                    if (this.props?.username) {
                        message.username = this.props.username;
                    }
                    if (this.props?.token) {
                        message.token = this.props.token;
                    }
                    if (this.props?.refreshToken) {
                        this.refreshToken();
                    }
                    this.ws.sendMessage(JSON.stringify(message));
                }
            };

            this.ws.onmessage = (event) => {
                // console.log('ws message received:', event.data);
                if (this.props?.setMessageCallback) {
                    this.props.setMessageCallback({
                        name: this.ws.name,
                        message: JSON.parse(event.data)
                    });
                }

                if (event.data === 'ping') {
                    this.ws.sendMessage('pong');
                }

                try {
                    const message = JSON.parse(event.data);
                    if (message?.error) {
                        this.setState({ reconnect: false });
                    }
                } catch (error) {

                }
            };

            this.ws.onclose = () => {
                // console.log('ws connection closed');
                this.setState({ readyState: this.ws.readyState });
            };

            this.ws.onerror = (error) => {
                console.error('ws error:', error);
                this.setState({ error: error });
            };

            this.ws.sendMessage = (message) => {
                if (this.ws.readyState === this.ws.OPEN) {
                    // console.log('ws message sent:', message);
                    this.ws.send(message);
                } else {
                    console.error('ws connection not open, unable to send message');
                }
            }

            if (this.props?.setWsCallback) {
                this.props.setWsCallback(this.ws);
            }
        }
    }

    deInit() {
        if (this?.ws) {
            if (this.ws.readyState === this.ws.OPEN) {
                this.ws.close();
                if (this.props?.setWsCallback) {
                    this.props.setWsCallback(null);
                }
            }
        }

        if (this.refreshTokenId) {
            clearTimeout(this.refreshTokenId);
            this.refreshTokenId = null;
        }
    }

    sendRefreshTokenRequest(timestamp) {
        localStorage.setItem('refreshTokenTimestamp', JSON.stringify(timestamp));
        this.ws.sendMessage(JSON.stringify({
            token: this.props.token,
            refresh_token: true
        }));
    }

    refreshToken() {
        if (!this.reconnectId) {
            try {
                this.reconnectId = setInterval(() => {
                    const timestamp = new Date().getTime();
                    const storageTimestamp = JSON.parse(localStorage.getItem('refreshTokenTimestamp'));
                    if (!storageTimestamp) {
                        this.sendRefreshTokenRequest(new Date().getTime());
                    } else if ((timestamp - storageTimestamp) > 1000 * 60 * 60) {
                        this.sendRefreshTokenRequest(timestamp);
                    }
                }, 5000);
            }
            catch (error) {
                this.sendRefreshTokenRequest(new Date().getTime());
            }
        }
    }

    reconnect(prevState) {
        if (prevState.readyState !== this.state.readyState && this.state.reconnect) {
            if (this.state.readyState === this.ws.CLOSED) {
                console.log('ws connection closed, reconnecting...');
                if (!this.reconnectId) {
                    this.reconnectId = setTimeout(() => {
                        this.reconnectId = null;
                        this.ws = null;
                        this.setState({ readyState: 0 });
                        this.init();
                    }, 1000);
                }
            }
        }
    }


    componentDidMount() {
        this.init();
    }

    componentDidUpdate(prevProps, prevState) {
        this.reconnect(prevState);
    }

    componentWillUnmount() {
        this.deInit();
    }


    render() {
        return (
            <>
            </>
        );
    }
}

export default WebSocketClient;